import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Globals } from '../global/globals';

export interface ConflictReviewer {
  reviewerId: number;
  reviewerName: string;
}

export interface ConflictQuestion {
  questionId: number;
  text: string;
  type: string;
  required: boolean;
  /** Choice option values for SINGLE_CHOICE / MULTI_CHOICE; empty for other types. */
  options: string[];
  reviewerAValue: string | null;
  reviewerBValue: string | null;
  conflict: boolean;
  piValue: string | null;
}

/** Mirrors the backend DataExtractionPaperDto. */
export interface DataExtractionPaper {
  paperToProjectId: number;
  paperId: number;
  title: string;
  publicationDate?: string;
  authors?: string;
  doi?: string;
  pdfId?: number;
  pdfUrl?: string;
}

export interface ConflictSummary {
  paper: DataExtractionPaper;
  resolved: boolean;
  conflictCount: number;
}

export interface ConflictDetail {
  paper: DataExtractionPaper;
  reviewerA: ConflictReviewer;
  reviewerB: ConflictReviewer;
  questions: ConflictQuestion[];
  readOnly: boolean;
  resolved: boolean;
}

export interface AnswerInput {
  questionId: number;
  value: string;
}

export interface CompletionStatus {
  papersNeedingSecondReviewer: number;
  unresolvedConflicts: number;
  canComplete: boolean;
}

/**
 * Shape returned by the reviewer-annotations endpoint. Mirrors the
 * annotation DTO used by HttpAnnotationsAdapter (claim-annotator/adapters/http-annotations.ts),
 * which declares the same shape as a private interface and so cannot be imported.
 */
export interface ClaimAnnotationDto {
  id: string;
  claimRef: string;
  type: string;
  text: string | null;
  note: string | null;
  ocrText: string | null;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  sessionId: string;
  createdAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class DataExtractionConflictService {
  private http = inject(HttpClient);
  private globals = inject(Globals);

  private base(projectId: number): string {
    return `${this.globals.backendUri}/projects/${projectId}/data-extraction`;
  }

  listConflicts(projectId: number): Observable<ConflictSummary[]> {
    return this.http.get<ConflictSummary[]>(`${this.base(projectId)}/conflicts`);
  }

  completionStatus(projectId: number): Observable<CompletionStatus> {
    return this.http.get<CompletionStatus>(`${this.base(projectId)}/completion-status`);
  }

  openConflict(projectId: number, ptpId: number): Observable<ConflictDetail> {
    return this.http.post<ConflictDetail>(`${this.base(projectId)}/conflicts/${ptpId}/open`, {});
  }

  resolveConflict(projectId: number, ptpId: number, answers: AnswerInput[]): Observable<void> {
    return this.http.post<void>(`${this.base(projectId)}/conflicts/${ptpId}/resolve`, answers);
  }

  completeDataExtraction(projectId: number): Observable<void> {
    return this.http.post<void>(`${this.base(projectId)}/complete`, {});
  }

  getReviewerAnnotations(
    projectId: number,
    paperId: number,
    authorId: number,
  ): Observable<ClaimAnnotationDto[]> {
    return this.http.get<ClaimAnnotationDto[]>(
      `${this.base(projectId)}/papers/${paperId}/annotations`,
      { params: { authorId } },
    );
  }
}
